import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { fetchAdminTreasury, runWalletReconciliation } from '../../services/walletApi';
import {
  TreasuryUltraAlert,
  TreasuryUltraBtn,
  TreasuryUltraShell,
  TreasuryUltraStat,
} from './TreasuryUltraShell';

export default function TreasuryWalletPanel({ onPendingCount = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reconBusy, setReconBusy] = useState(false);
  const [reconMessage, setReconMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const summary = await fetchAdminTreasury();
      setData(summary);
      if (onPendingCount) onPendingCount(summary.pendingSettlementCount || 0);
    } catch (err) {
      setData(null);
      setError(err.message || 'Failed to load treasury');
      if (onPendingCount) onPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [onPendingCount]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <TreasuryUltraShell
        title="DE Treasury Wallet"
        subtitle="Client booking escrow held until settlement or refund"
        icon={String.fromCodePoint(0x1F3E6)}
        accent="gold"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((k) => (
            <div key={k} className="de-treasury-ultra-skeleton" />
          ))}
        </div>
      </TreasuryUltraShell>
    );
  }

  if (error) {
    return (
      <TreasuryUltraShell
        title="DE Treasury Wallet"
        subtitle="Escrow configuration required"
        icon={String.fromCodePoint(0x1F3E6)}
        accent="amber"
      >
        <TreasuryUltraAlert tone="warn">
          <p className="m-0 font-semibold">Treasury not available</p>
          <p className="m-0 mt-1 opacity-90">{error}</p>
          <p className="m-0 mt-2 text-[10px] text-de-muted">
            Set <code className="text-amber-200">DE_TREASURY_USER_ID</code> on Railway and run migration{' '}
            <code className="text-amber-200">20260605_payment_flow_escrow_settlement.sql</code>.
          </p>
        </TreasuryUltraAlert>
        <TreasuryUltraBtn variant="gold" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </TreasuryUltraBtn>
      </TreasuryUltraShell>
    );
  }

  const latestRecon = data.latestReconciliation;
  const reconOk = latestRecon?.status === 'ok';

  return (
    <TreasuryUltraShell
      title="DE Treasury Wallet"
      subtitle="Dedicated escrow account — not your daily admin login"
      icon={String.fromCodePoint(0x1F3E6)}
      accent="gold"
      badge={data.isDedicatedEscrow ? 'DEDICATED' : 'CHECK FLAG'}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="m-0 break-all font-mono text-[10px] text-de-gold/70">
            {data.treasuryUserId}
          </p>
          <div className="flex gap-2">
            <TreasuryUltraBtn
              variant="gold"
              disabled={reconBusy}
              onClick={async () => {
                setReconBusy(true);
                setReconMessage('');
                try {
                  const r = await runWalletReconciliation();
                  setReconMessage(r.status === 'ok' ? 'Reconciliation OK' : `Mismatch: ${r.mismatch_count || r.mismatches?.length || 0} issue(s)`);
                  await load();
                } catch (err) {
                  setReconMessage(err.message || 'Reconciliation failed');
                } finally {
                  setReconBusy(false);
                }
              }}
            >
              Run recon
            </TreasuryUltraBtn>
            <TreasuryUltraBtn variant="ghost" onClick={load}>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </TreasuryUltraBtn>
          </div>
        </div>
      )}
    >
      {!data.isDedicatedEscrow ? (
        <TreasuryUltraAlert tone="warn">
          Treasury user must have <code>is_treasury_escrow=true</code> in Supabase. Do not use personal admin login.
        </TreasuryUltraAlert>
      ) : null}

      {latestRecon ? (
        <TreasuryUltraAlert tone={reconOk ? 'success' : 'error'}>
          Last recon {new Date(latestRecon.run_at).toLocaleString('en-IN')}: {reconOk ? 'ALL WALLETS OK' : `${latestRecon.mismatch_count} mismatch(es)`}
          {latestRecon.treasury_delta != null && !reconOk ? ` · Treasury Δ ₹${Number(latestRecon.treasury_delta).toLocaleString('en-IN')}` : ''}
          {(latestRecon.client_wallet_mismatch_count > 0 || latestRecon.owner_wallet_mismatch_count > 0) ? (
            <span className="block mt-1 text-[11px]">
              Client wallets: {latestRecon.client_wallet_mismatch_count || 0} drift · Owner wallets: {latestRecon.owner_wallet_mismatch_count || 0} drift
            </span>
          ) : null}
        </TreasuryUltraAlert>
      ) : (
        <TreasuryUltraAlert tone="info">Daily reconciliation: treasury + client + owner wallets. Run manually after migration 20260608.</TreasuryUltraAlert>
      )}

      {reconMessage ? <TreasuryUltraAlert tone="info">{reconMessage}</TreasuryUltraAlert> : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TreasuryUltraStat
          tone="gold"
          label="Treasury Balance"
          value={`₹${Number(data.balance || 0).toLocaleString('en-IN')}`}
          sub={`${data.adminName || 'Admin'} · ${data.configuredFromEnv ? 'env configured' : 'auto-detected'}`}
        />
        <TreasuryUltraStat
          tone="sky"
          label="Active Escrow Held"
          value={`₹${Number(data.activeEscrowHeld || 0).toLocaleString('en-IN')}`}
          sub="Sum of Active booking advances"
        />
        <TreasuryUltraStat
          tone="amber"
          label="Pending Transfers"
          value={String(data.pendingSettlementCount || 0)}
          sub="Treasury low at complete time"
        />
        <TreasuryUltraStat
          tone="muted"
          label="Wallets Checked"
          value={String((latestRecon?.client_wallets_checked || 0) + (latestRecon?.owner_wallets_checked || 0))}
          sub={`Client ${latestRecon?.client_wallets_checked || 0} · Owner ${latestRecon?.owner_wallets_checked || 0}`}
        />
      </div>
    </TreasuryUltraShell>
  );
}
